import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { brand } from '../../config/brand';
import RobotLogo from '../RobotLogo/RobotLogo';
import { usePageProgress } from '../../hooks/useScrollProgress';
import './Nav.css';

const Nav: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const linksRef = useRef<HTMLElement>(null);
  const progress = usePageProgress();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Close the drawer on Escape, like a sheet
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // IntersectionObserver for active link highlight
  useEffect(() => {
    const sections = document.querySelectorAll('section[id]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  /* The active-link indicator is a single glass pill that slides between
     links rather than a dot per link — the iOS way of showing selection. */
  const positionPill = useCallback(() => {
    const nav = linksRef.current;
    if (!nav) return;
    const active = nav.querySelector<HTMLElement>('.nav__link--active');
    if (!active) {
      nav.style.setProperty('--pill-o', '0');
      return;
    }
    nav.style.setProperty('--pill-x', `${active.offsetLeft}px`);
    nav.style.setProperty('--pill-w', `${active.offsetWidth}px`);
    nav.style.setProperty('--pill-o', '1');
  }, []);

  useLayoutEffect(positionPill, [activeSection, scrolled, positionPill]);

  useEffect(() => {
    const nav = linksRef.current;
    if (!nav) return;
    const observer = new ResizeObserver(positionPill);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [positionPill]);

  return (
    <header className={`nav${scrolled ? ' nav--scrolled' : ''}`} role="banner">
      <div className="nav__inner container">
        <span
          className="nav__progress"
          style={{ transform: `scaleX(${progress})` }}
          aria-hidden="true"
        />

        <a href="#hero" className="nav__logo" aria-label="ZinifyLabs home">
          <RobotLogo size={32} className="nav__logo-icon" />
          <span className="nav__logo-text">ZinifyLabs</span>
        </a>

        <nav className="nav__links" role="navigation" aria-label="Main navigation" ref={linksRef}>
          <span className="nav__pill" aria-hidden="true" />
          {brand.nav.links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={`nav__link${activeSection === link.href.slice(1) ? ' nav__link--active' : ''}`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <a href="#contact" className="btn btn-primary nav__cta" id="nav-cta-btn">
          {brand.agency.ctaPrimary}
        </a>

        <button
          className={`nav__hamburger${menuOpen ? ' nav__hamburger--open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
          id="nav-hamburger-btn"
        >
          <span /><span /><span />
        </button>
      </div>

      {/* Mobile drawer — a glass sheet that slides down over the page */}
      <div className={`nav__drawer${menuOpen ? ' nav__drawer--open' : ''}`} role="dialog" aria-label="Mobile navigation" aria-modal="true">
        <nav>
          {brand.nav.links.map((link, i) => (
            <a
              key={link.href}
              href={link.href}
              className="nav__drawer-link"
              style={{ '--i': i } as React.CSSProperties}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
            </a>
          ))}
          <a
            href="#contact"
            className="btn btn-primary nav__drawer-cta"
            style={{ '--i': brand.nav.links.length } as React.CSSProperties}
            onClick={() => setMenuOpen(false)}
          >
            {brand.agency.ctaPrimary}
          </a>
        </nav>
      </div>

      {menuOpen && (
        <div className="nav__backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />
      )}
    </header>
  );
};

export default Nav;
